import {
  Box,
  FormControl,
  OutlinedInput,
  FormHelperText,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { he } from 'date-fns/locale';
import { FC, useEffect, useState } from 'react';
import { TextFieldProps } from 'src/models/FieldProps.interface';
import { CSSProperties } from 'styled-components';

const ContainerBox = styled(Box)<{ mt?: number }>(({ mt = 0 }) => ({
  marginBottom: 18,
  marginTop: mt,
}));

const LabelTypography = styled(Typography)<{
  fontSize?: string;
  color?: string;
}>(({ fontSize = '16px', color = 'var(--primary-navy)' }) => ({
  fontFamily: 'Outfit',
  fontWeight: '400',
  fontSize,
  color,
  marginBottom: 16,
}));

const OptionalText = styled('span')({
  fontFamily: 'Outfit',
  fontWeight: '400',
  fontSize: '14px',
  color: 'var(--instructions)',
  fontStyle: 'italic',
  marginLeft: 8,
});

const StyledFormControl = styled(FormControl)({
  margin: 0,
});

const StyledOutlinedInput = styled(OutlinedInput)<{
  height?: string;
  width?: string;
  hasError?: boolean;
  overflowY?: CSSProperties['overflowY'];
}>(
  ({
    height = '60px',
    width = '480px',
    hasError = false,
    overflowY = 'auto',
  }) => ({
    height,
    width,
    backgroundColor: 'var(--text-bg)',
    border: 'none',
    borderRadius: 4,
    overflow: 'hidden',
    position: 'relative',
    alignItems: 'flex-start',
    '& .MuiInputBase-inputMultiline': {
      overflowY: `${overflowY} !important`,
      maxHeight: '100%',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? 'var(--error)' : 'var(--border)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--primary-blue)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--primary-blue)',
    },
    '& textarea': {
      boxSizing: 'border-box',
      resize: 'none',
      fontFamily: 'Outfit',
      fontSize: '16px',
      overflowY: overflowY,
      '&::-webkit-scrollbar': {
        width: '6px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'var(--bg-color)',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'var(--loader-color)',
        borderRadius: '4px',
        height: '25px',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: 'var(--primary-blue)',
        cursor: 'pointer',
      },
    },
    '& textarea:-webkit-autofill': {
      backgroundColor: 'var(--bg-color)',
      WebkitBoxShadow: '0 0 0 30px var(--bg-color) inset',
    },
    '& textarea:-moz-autofill': {
      backgroundColor: 'var(--bg-color)',
      boxShadow: '0 0 0 30px var(--bg-color) inset',
    },
    '& textarea:-ms-autofill': {
      backgroundColor: 'var(--bg-color)',
      boxShadow: '0 0 0 30px var(--bg-color) inset',
    },
  })
);

const StyledFormHelperText = styled(FormHelperText)({
  fontFamily: 'Outfit',
  fontWeight: '400',
  fontSize: '14px',
  color: 'var(--error)',
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  marginLeft: 0,
  bottom: '-22px',
  marginTop: 8,
});

const ErrorIcon = styled(Box)({
  fontSize: '16px',
  color: 'var(--error)',
  marginRight: 6,
});

const TextArea: FC<TextFieldProps> = ({
  id,
  label,
  formikProps,
  value,
  onChange,
  errorState = false,
  isEditingOwnAccount,
  mt = 0,
  height = '60px',
  width = '480px',
  rows,
  customValidation,
  color = 'var(--primary-navy)',
  fontSize = '16px',
  optional = false,
  overflowY = 'auto',
}) => {
  const hasFormikProps =
    formikProps && formikProps.touched && formikProps.errors;
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (customValidation) {
      const validationError = customValidation(value);
      setLocalError(validationError || null);
    }
  }, [value, customValidation]);

  const errorMessage =
    (hasFormikProps && formikProps.touched[id] && formikProps.errors[id]) ||
    localError ||
    errorState;

  const hasError =
    (hasFormikProps &&
      formikProps.touched[id] &&
      Boolean(formikProps.errors[id])) ||
    Boolean(errorState);

  return (
    <ContainerBox mt={mt}>
      <LabelTypography fontSize={fontSize} color={color}>
        {label}
        {optional && <OptionalText>Optional</OptionalText>}
      </LabelTypography>
      <StyledFormControl fullWidth error={Boolean(errorMessage)}>
        <StyledOutlinedInput
          id={id}
          multiline
          rows={rows}
          value={value}
          onChange={onChange}
          height={height}
          width={width}
          hasError={hasError}
          overflowY={overflowY}
          inputProps={{
            readOnly: isEditingOwnAccount,
          }}
          {...formikProps?.getFieldProps(id)}
        />
        {errorMessage && (
          <StyledFormHelperText>
            <ErrorIcon component="i" className="bx bxs-error-circle" />
            {errorMessage}
          </StyledFormHelperText>
        )}
      </StyledFormControl>
    </ContainerBox>
  );
};

export default TextArea;
